import { useState } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { useLang } from "../context/LangContext"

const CONTENT = {
  en: {
    title: "Frequently Asked Questions",
    sub: "Everything you need to know about uCloud360.",
    categories: [
      {
        cat: "About the Platform",
        items: [
          {
            q: "What is uCloud360?",
            a: "uCloud360 is a digital incubation and acceleration platform designed to support Hispanic entrepreneurs in the United States. It acts as a collaborative ecosystem where entrepreneurs can share expertise, access tools, and grow their businesses together."
          },
          {
            q: "Who is uCloud360 for?",
            a: "uCloud360 is designed for Hispanic entrepreneurs in the United States — those with innovative ideas, projects in development, or businesses already running. Whether you're just starting out or looking to scale, you'll find the support and resources you need here."
          },
          {
            q: "How does the reciprocal mentorship model work?",
            a: "Every entrepreneur is an expert in their area. On uCloud360, a finance expert can get tech guidance from a technology entrepreneur, who in turn gets financial advice. This mutual support system means no project stops for lack of technical or administrative expertise."
          },
          {
            q: "What makes uCloud360 different from other platforms?",
            a: "uCloud360 is specifically built for the Hispanic entrepreneurial community in the US. It combines a social network, collaborative tools, built-in fintech utilities, specialty channels, and a mentorship program — all in one platform with both Spanish and English support."
          },
        ]
      },
      {
        cat: "Features & Tools",
        items: [
          {
            q: "What are the Specialty Channels?",
            a: "Specialty Channels are one of the most important sections of the community. Organized by area of expertise, they allow entrepreneurs to offer and receive peer-to-peer advice in: Technology, Finance, Logistics, Legal, and Procedures. Access requires an active subscription."
          },
          {
            q: "What is Mentorías 121 (1:1 Mentorship)?",
            a: "Mentorías 121 is an exclusive service for subscribers offering private, personalized sessions via one-on-one video meetings with high-level experts. It's designed for critical moments when you need deep, focused guidance to unblock the next stage of your business."
          },
          {
            q: "What built-in tools are available?",
            a: "uCloud360 includes: a Compound Interest Calculator for investment projections, a Time Tracker for work hour management, a Coin Market Tracker with real-time crypto data (via CoinGecko), and an Economic Calendar powered by Investing.com."
          },
          {
            q: "Do I need an account to use the tools?",
            a: "The free tools (Compound Interest Calculator, Coin Market Tracker, Economic Calendar) are accessible without an account. The Time Tracker and community features require registration."
          },
        ]
      },
      {
        cat: "Membership & Pricing",
        items: [
          {
            q: "Is there a free plan?",
            a: "Yes. The Free plan gives you access to all built-in tools, public pages, and basic account features — forever, at no cost."
          },
          {
            q: "What does the Plus plan include?",
            a: "The Plus plan ($9.99/month) includes everything in Free, plus access to the private Discord community, all specialty channels, early feature access, and community support."
          },
          {
            q: "What does the Premium plan include?",
            a: "The Premium plan ($29.99/month) includes everything in Plus, plus exclusive discounts on 1:1 mentorship sessions, premium technology tools, and priority invitations to in-person networking events."
          },
          {
            q: "Can I cancel my subscription?",
            a: "Yes, you can cancel at any time from your dashboard. You'll keep access until the end of your current billing period."
          },
          {
            q: "How are payments processed?",
            a: "Payments are handled securely by Stripe. uCloud360 never stores your card details."
          },
        ]
      },
      {
        cat: "Getting Started",
        items: [
          {
            q: "How do I join uCloud360?",
            a: "Simply click 'Join free' on the homepage and create your account with email or via Google, Microsoft, or Facebook. It takes less than 2 minutes."
          },
          {
            q: "I'm new to entrepreneurship. Is uCloud360 right for me?",
            a: "Absolutely. uCloud360 is designed precisely for entrepreneurs at the beginning of their journey — those who feel overwhelmed by not knowing where to start. The platform exists to demystify entrepreneurship and provide a clear roadmap with community support."
          },
          {
            q: "What questions does uCloud360 help me answer?",
            a: "Common questions our members come in with: Do I need an app or a website? How do I handle taxes for my business? How much capital will I need? Can I import this product? Where can I store inventory? How do I invoice my products/services? How can I control my inventory?"
          },
          {
            q: "How do I contact uCloud360?",
            a: "You can reach us at info@ucloud360.com or by calling (786) 451-4979. We're based in Miami, FL 33178."
          },
        ]
      },
    ]
  },
  es: {
    title: "Preguntas Frecuentes",
    sub: "Todo lo que necesitas saber sobre uCloud360.",
    categories: [
      {
        cat: "Sobre la Plataforma",
        items: [
          {
            q: "¿Qué es uCloud360?",
            a: "uCloud360 es una plataforma digital de incubación y aceleración diseñada para apoyar a los emprendedores hispanos en los Estados Unidos. Funciona como un ecosistema colaborativo donde los emprendedores pueden compartir experiencia, acceder a herramientas y hacer crecer sus negocios juntos."
          },
          {
            q: "¿Para quién es uCloud360?",
            a: "uCloud360 está diseñado para emprendedores hispanos en los Estados Unidos — los que tienen ideas innovadoras, proyectos en desarrollo o negocios en marcha. Ya sea que estés comenzando o buscando escalar, aquí encontrarás el apoyo y los recursos que necesitas."
          },
          {
            q: "¿Cómo funciona el modelo de mentoría recíproca?",
            a: "Cada emprendedor es experto en su área. En uCloud360, un experto en finanzas puede recibir orientación tecnológica de un emprendedor tech, quien a su vez recibe asesoría financiera. Este sistema de apoyo mutuo garantiza que ningún proyecto se detenga por falta de asesoría técnica o administrativa."
          },
          {
            q: "¿Qué diferencia a uCloud360 de otras plataformas?",
            a: "uCloud360 está construido específicamente para la comunidad emprendedora hispana en EE. UU. Combina una red social, herramientas de colaboración, utilidades fintech integradas, canales de especialidad y un programa de mentoría — todo en una plataforma con soporte en español e inglés."
          },
        ]
      },
      {
        cat: "Funciones y Herramientas",
        items: [
          {
            q: "¿Qué son los Canales de Especialidad?",
            a: "Los Canales de Especialidad son una de las secciones más importantes de la comunidad. Organizados por área de experiencia, permiten a los emprendedores ofrecer y recibir asesoría entre pares en: Tecnología, Finanzas, Logística, Legal y Procedimientos. El acceso requiere suscripción activa."
          },
          {
            q: "¿Qué son las Mentorías 121?",
            a: "Las Mentorías 121 son un servicio exclusivo para suscriptores que ofrece sesiones privadas y personalizadas a través de videollamadas uno a uno con expertos de alto nivel. Están diseñadas para momentos críticos donde necesitas orientación profunda y enfocada para desbloquear el siguiente nivel de tu empresa."
          },
          {
            q: "¿Qué herramientas incluidas están disponibles?",
            a: "uCloud360 incluye: Calculadora de Interés Compuesto, Time Tracker para gestión de horas, Rastreador de Mercado Cripto con datos en tiempo real (via CoinGecko) y Calendario Económico powered by Investing.com."
          },
          {
            q: "¿Necesito cuenta para usar las herramientas?",
            a: "Las herramientas gratuitas (Calculadora de Interés Compuesto, Mercado Cripto, Calendario Económico) son accesibles sin cuenta. El Time Tracker y las funciones comunitarias requieren registro."
          },
        ]
      },
      {
        cat: "Membresía y Precios",
        items: [
          {
            q: "¿Hay un plan gratuito?",
            a: "Sí. El plan Gratis da acceso a todas las herramientas incluidas, páginas públicas y funciones básicas de cuenta — para siempre, sin costo."
          },
          {
            q: "¿Qué incluye el plan Plus?",
            a: "El plan Plus ($9.99/mes) incluye todo lo del plan Gratis, más acceso a la comunidad privada de Discord, todos los canales de especialidad, acceso anticipado a nuevas funciones y soporte comunitario."
          },
          {
            q: "¿Qué incluye el plan Premium?",
            a: "El plan Premium ($29.99/mes) incluye todo lo del plan Plus, más descuentos exclusivos en sesiones de mentoría 1:1, herramientas tecnológicas premium e invitaciones prioritarias a eventos de networking presenciales."
          },
          {
            q: "¿Puedo cancelar mi suscripción?",
            a: "Sí, puedes cancelar en cualquier momento desde tu dashboard. Mantendrás el acceso hasta el final de tu período de facturación actual."
          },
          {
            q: "¿Cómo se procesan los pagos?",
            a: "Los pagos son procesados de forma segura por Stripe. uCloud360 nunca almacena los datos de tu tarjeta."
          },
        ]
      },
      {
        cat: "Comenzando",
        items: [
          {
            q: "¿Cómo me uno a uCloud360?",
            a: "Simplemente haz clic en 'Únete gratis' en la página principal y crea tu cuenta con email o a través de Google, Microsoft o Facebook. Toma menos de 2 minutos."
          },
          {
            q: "Soy nuevo en el emprendimiento. ¿Es uCloud360 para mí?",
            a: "Absolutamente. uCloud360 está diseñado precisamente para emprendedores al inicio de su camino — los que se sienten abrumados por no saber por dónde empezar. La plataforma existe para desmitificar el emprendimiento y proporcionar una hoja de ruta clara con apoyo comunitario."
          },
          {
            q: "¿Qué preguntas me ayuda a responder uCloud360?",
            a: "Preguntas comunes con las que llegan nuestros miembros: ¿Necesito una app o una web? ¿Cómo manejo los impuestos de mi negocio? ¿Cuánto capital necesitaré? ¿Puedo importar este producto? ¿Dónde puedo almacenar mi mercancía? ¿Cómo puedo facturar mis productos/servicios? ¿Cómo puedo controlar mi inventario?"
          },
          {
            q: "¿Cómo contacto a uCloud360?",
            a: "Puedes contactarnos en info@ucloud360.com o llamando al (786) 451-4979. Estamos ubicados en Miami, FL 33178."
          },
        ]
      },
    ]
  }
}

export default function FAQsPage() {
  const { lang } = useLang()
  const t = CONTENT[lang]
  const [open, setOpen] = useState<string | null>(null)

  const toggle = (key: string) => setOpen(open === key ? null : key)

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white">
      {/* Hero */}
      <section className="bg-[#F8FAFF] dark:bg-neutral-900/50 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 className="text-4xl font-semibold tracking-tight">{t.title}</h1>
          <p className="mt-3 text-gray-500 dark:text-gray-400">{t.sub}</p>
        </div>
      </section>

      {/* FAQ content */}
      <div className="max-w-3xl mx-auto px-6 py-14 space-y-12">
        {t.categories.map((cat) => (
          <div key={cat.cat}>
            <h2 className="text-xs font-semibold tracking-widest uppercase text-[#2563EB] mb-6">{cat.cat}</h2>
            <div className="space-y-2">
              {cat.items.map((item) => {
                const key = `${cat.cat}-${item.q}`
                const isOpen = open === key
                return (
                  <div key={key} className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggle(key)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left bg-white dark:bg-neutral-900 hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
                    >
                      <span className="font-medium text-sm pr-4 text-black dark:text-white">{item.q}</span>
                      {isOpen
                        ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      }
                    </button>
                    {isOpen && (
                      <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-neutral-900">
                        <p className="text-sm leading-7 text-gray-600 dark:text-gray-300">{item.a}</p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {/* Contact CTA */}
        <div className="rounded-2xl bg-[#F8FAFF] dark:bg-neutral-900 border border-gray-200 dark:border-gray-800 p-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {lang === "en" ? "Still have questions?" : "¿Tienes más preguntas?"}
          </p>
          <a
            href="mailto:info@ucloud360.com"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1d4ed8] transition"
          >
            info@ucloud360.com
          </a>
        </div>
      </div>
    </div>
  )
}
